// src/hooks/useDocumentSearch.ts
import { useState, useCallback } from 'react';
import type {
  SearchResultItem,
  MetadataFilter
} from '@/types/search';

interface UseDocumentSearchOptions {
  initialQuery?: string;
  autoSearch?: boolean;
  debounceMs?: number;
  defaultFilters?: MetadataFilter[];
}

interface UseDocumentSearchReturn {
  documents: SearchResultItem[];
  totalResults: number;
  searchTime: number;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  facets: Record<string, any>;
  isSearchMode: boolean;
  searchQuery: string;
  selectedFilters: MetadataFilter[];
  setSearchQuery: (query: string) => void;
  executeSearch: (overrideQuery?: string) => Promise<void>;
  addFilter: (filter: MetadataFilter) => void;
  removeFilter: (filter: MetadataFilter) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
}

export function useDocumentSearch({
  initialQuery = '',
  defaultFilters = []
}: UseDocumentSearchOptions = {}): UseDocumentSearchReturn {
  const [documents] = useState<SearchResultItem[]>([]);
  const [totalResults] = useState(0);
  const [searchTime] = useState(0);
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedFilters, setSelectedFilters] = useState<MetadataFilter[]>(defaultFilters);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages] = useState(0);
  const [facets] = useState<Record<string, any>>({});

  const isSearchMode = searchQuery.length >= 2;

  // Disabled - no API calls
  const executeSearch = useCallback(async (_overrideQuery?: string) => {
    console.log('Search is currently disabled - Coming Soon');
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(0);
  }, []);

  const addFilter = useCallback((filter: MetadataFilter) => {
    setSelectedFilters(prev => {
      const exists = prev.some(f =>
        f.field === filter.field &&
        f.value === filter.value &&
        f.operator === filter.operator
      );
      if (exists) return prev;
      return [...prev, filter];
    });
    setCurrentPage(0);
  }, []);

  const removeFilter = useCallback((filterToRemove: MetadataFilter) => {
    setSelectedFilters(prev =>
      prev.filter(filter =>
        !(filter.field === filterToRemove.field &&
          filter.value === filterToRemove.value)
      )
    );
    setCurrentPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedFilters([]);
    setCurrentPage(0);
  }, []);

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    documents,
    totalResults,
    loading,
    error,
    searchTime,
    currentPage,
    totalPages,
    facets,
    isSearchMode,
    searchQuery,
    selectedFilters,
    setSearchQuery: handleSearchQueryChange,
    executeSearch,
    addFilter,
    removeFilter,
    clearFilters,
    setPage,
  };
}
